import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { suppliersAPI, purchaseOrdersAPI, productsAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Truck, Package, Phone, EnvelopeSimple, MapPin, X, Check, Clock, CaretRight } from '@phosphor-icons/react';

const orderStatuses = [
  { value: 'pending', label: 'Beklemede', color: 'bg-accent/20 text-accent-foreground' },
  { value: 'confirmed', label: 'Onaylandı', color: 'bg-blue-100 text-blue-700' },
  { value: 'shipped', label: 'Yolda', color: 'bg-purple-100 text-purple-700' },
  { value: 'delivered', label: 'Teslim Edildi', color: 'bg-secondary/20 text-secondary' },
  { value: 'cancelled', label: 'İptal', color: 'bg-destructive/20 text-destructive' },
];

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('suppliers');
  
  // Supplier dialog
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({
    name: '', contact_person: '', phone: '', email: '', address: '', notes: ''
  });
  
  // Order dialog
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderForm, setOrderForm] = useState({
    supplier_id: '', expected_date: '', notes: '', items: []
  });
  const [orderItemForm, setOrderItemForm] = useState({ product_id: '', quantity: '', unit_price: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [suppliersRes, ordersRes, productsRes] = await Promise.all([
        suppliersAPI.getAll(false),
        purchaseOrdersAPI.getAll(),
        productsAPI.getAll(),
      ]);
      setSuppliers(suppliersRes.data);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Supplier handlers
  const handleOpenSupplierDialog = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setSupplierForm({
        name: supplier.name,
        contact_person: supplier.contact_person || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        address: supplier.address || '',
        notes: supplier.notes || '',
      });
    } else {
      setEditingSupplier(null);
      setSupplierForm({ name: '', contact_person: '', phone: '', email: '', address: '', notes: '' });
    }
    setSupplierDialogOpen(true);
  };

  const handleSubmitSupplier = async (e) => {
    e.preventDefault();
    if (!supplierForm.name) {
      toast.error('Tedarikçi adı gerekli');
      return;
    }
    
    try {
      if (editingSupplier) {
        await suppliersAPI.update(editingSupplier.id, supplierForm);
        toast.success('Tedarikçi güncellendi');
      } else {
        await suppliersAPI.create(supplierForm);
        toast.success('Tedarikçi eklendi');
      }
      setSupplierDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!window.confirm('Bu tedarikçiyi silmek istediğinizden emin misiniz?')) return;
    try {
      await suppliersAPI.delete(id);
      toast.success('Tedarikçi silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  // Order handlers
  const handleOpenOrderDialog = () => {
    setOrderForm({ supplier_id: '', expected_date: '', notes: '', items: [] });
    setOrderItemForm({ product_id: '', quantity: '', unit_price: '' });
    setOrderDialogOpen(true);
  };

  const handleAddOrderItem = () => {
    if (!orderItemForm.product_id || !orderItemForm.quantity || !orderItemForm.unit_price) {
      toast.error('Tüm alanları doldurun');
      return;
    }
    const product = products.find(p => p.id === orderItemForm.product_id);
    setOrderForm({
      ...orderForm,
      items: [...orderForm.items, {
        product_id: orderItemForm.product_id,
        product_name: product?.name,
        quantity: parseFloat(orderItemForm.quantity),
        unit_price: parseFloat(orderItemForm.unit_price),
      }]
    });
    setOrderItemForm({ product_id: '', quantity: '', unit_price: '' });
  };

  const handleRemoveOrderItem = (index) => {
    setOrderForm({
      ...orderForm,
      items: orderForm.items.filter((_, i) => i !== index)
    });
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!orderForm.supplier_id || orderForm.items.length === 0) {
      toast.error('Tedarikçi seçin ve en az bir ürün ekleyin');
      return;
    }
    
    try {
      await purchaseOrdersAPI.create({
        supplier_id: orderForm.supplier_id,
        expected_date: orderForm.expected_date || undefined,
        notes: orderForm.notes || undefined,
        items: orderForm.items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          unit_price: i.unit_price,
        })),
      });
      toast.success('Sipariş oluşturuldu');
      setOrderDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sipariş oluşturulamadı');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await purchaseOrdersAPI.update(orderId, { status: newStatus });
      toast.success(newStatus === 'delivered' ? 'Sipariş teslim alındı, stok güncellendi' : 'Durum güncellendi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const getStatusBadge = (status) => {
    const s = orderStatuses.find(os => os.value === status) || orderStatuses[0];
    return <span className={`badge-pill ${s.color}`}>{s.label}</span>;
  };

  const orderTotal = orderForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
              Tedarikçiler & Siparişler
            </h1>
            <p className="mt-2 text-muted-foreground">
              Tedarikçi yönetimi ve satın alma siparişleri
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => handleOpenSupplierDialog()} variant="outline" data-testid="add-supplier-btn">
              <Plus size={20} className="mr-2" />
              Tedarikçi Ekle
            </Button>
            <Button onClick={handleOpenOrderDialog} className="bg-primary hover:bg-primary/90" data-testid="add-order-btn">
              <Truck size={20} className="mr-2" />
              Yeni Sipariş
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="suppliers" data-testid="tab-suppliers">Tedarikçiler</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Siparişler</TabsTrigger>
          </TabsList>

          <TabsContent value="suppliers" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suppliers.length === 0 ? (
                <div className="col-span-full text-center py-12 border border-border bg-card rounded-xl">
                  <Truck size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">Henüz tedarikçi bulunmuyor</p>
                </div>
              ) : (
                suppliers.map((supplier) => (
                  <div
                    key={supplier.id}
                    className={`border bg-card p-5 rounded-xl card-hover ${!supplier.is_active ? 'opacity-50' : ''}`}
                    data-testid={`supplier-card-${supplier.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-heading text-lg font-semibold">{supplier.name}</h3>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenSupplierDialog(supplier)}>
                          <PencilSimple size={16} />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteSupplier(supplier.id)}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </div>
                    {supplier.contact_person && (
                      <p className="text-sm text-muted-foreground mb-2">{supplier.contact_person}</p>
                    )}
                    <div className="space-y-1 text-sm">
                      {supplier.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone size={14} /> {supplier.phone}
                        </div>
                      )}
                      {supplier.email && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <EnvelopeSimple size={14} /> {supplier.email}
                        </div>
                      )}
                      {supplier.address && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin size={14} /> {supplier.address}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <div className="border border-border bg-card rounded-xl overflow-hidden">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="border-b border-border bg-muted/30">
                    <TableHead className="py-4">Sipariş</TableHead>
                    <TableHead>Tedarikçi</TableHead>
                    <TableHead>Ürünler</TableHead>
                    <TableHead className="text-right">Toplam</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead>Tarih</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <Package size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">Henüz sipariş bulunmuyor</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow key={order.id} className="table-row-hover border-b border-border/40" data-testid={`order-row-${order.id}`}>
                        <TableCell className="py-3 font-mono text-sm">#{order.id.slice(0, 8)}</TableCell>
                        <TableCell className="font-medium">{order.supplier_name}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {order.items.slice(0, 2).map((item, idx) => (
                              <span key={idx} className="text-xs bg-muted px-2 py-1 rounded">
                                {item.product_name} x{item.quantity}
                              </span>
                            ))}
                            {order.items.length > 2 && (
                              <span className="text-xs bg-muted px-2 py-1 rounded">+{order.items.length - 2}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-data">{formatCurrency(order.total_amount)}</TableCell>
                        <TableCell>{getStatusBadge(order.status)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(order.created_at)}</TableCell>
                        <TableCell className="text-right">
                          {order.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'confirmed')}>
                              Onayla
                            </Button>
                          )}
                          {order.status === 'confirmed' && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'shipped')}>
                              Yolda
                            </Button>
                          )}
                          {order.status === 'shipped' && (
                            <Button size="sm" className="bg-secondary hover:bg-secondary/90" onClick={() => handleUpdateOrderStatus(order.id, 'delivered')}>
                              <Check size={16} className="mr-1" /> Teslim Al
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        {/* Supplier Dialog */}
        <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingSupplier ? 'Tedarikçi Düzenle' : 'Yeni Tedarikçi'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitSupplier} className="space-y-4">
              <div className="space-y-2">
                <Label>Firma Adı *</Label>
                <Input
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                  placeholder="ABC Gıda Ltd."
                  data-testid="supplier-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Yetkili Kişi</Label>
                  <Input
                    value={supplierForm.contact_person}
                    onChange={(e) => setSupplierForm({ ...supplierForm, contact_person: e.target.value })}
                    placeholder="Ahmet Yılmaz"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <Input
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                    placeholder="0532 123 45 67"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>E-posta</Label>
                <Input
                  type="email"
                  value={supplierForm.email}
                  onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                  placeholder="info@abc.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Adres</Label>
                <Textarea
                  value={supplierForm.address}
                  onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                  placeholder="Adres..."
                  rows={2}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSupplierDialogOpen(false)}>İptal</Button>
                <Button type="submit" data-testid="supplier-submit">{editingSupplier ? 'Güncelle' : 'Ekle'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Order Dialog */}
        <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Yeni Satın Alma Siparişi</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitOrder} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tedarikçi *</Label>
                  <Select value={orderForm.supplier_id} onValueChange={(v) => setOrderForm({ ...orderForm, supplier_id: v })}>
                    <SelectTrigger data-testid="order-supplier-select">
                      <SelectValue placeholder="Tedarikçi seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.filter(s => s.is_active).map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Beklenen Teslimat</Label>
                  <Input
                    type="date"
                    value={orderForm.expected_date}
                    onChange={(e) => setOrderForm({ ...orderForm, expected_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Ürün Ekle</Label>
                <div className="flex gap-2">
                  <Select value={orderItemForm.product_id} onValueChange={(v) => setOrderItemForm({ ...orderItemForm, product_id: v })}>
                    <SelectTrigger className="flex-1" data-testid="order-product-select">
                      <SelectValue placeholder="Ürün seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Miktar"
                    className="w-24"
                    value={orderItemForm.quantity}
                    onChange={(e) => setOrderItemForm({ ...orderItemForm, quantity: e.target.value })}
                  />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Fiyat"
                    className="w-24"
                    value={orderItemForm.unit_price}
                    onChange={(e) => setOrderItemForm({ ...orderItemForm, unit_price: e.target.value })}
                  />
                  <Button type="button" onClick={handleAddOrderItem} data-testid="add-order-item-btn">
                    <Plus size={18} />
                  </Button>
                </div>
              </div>

              {orderForm.items.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Ürün</TableHead>
                        <TableHead className="text-right">Miktar</TableHead>
                        <TableHead className="text-right">Birim Fiyat</TableHead>
                        <TableHead className="text-right">Toplam</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orderForm.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell className="text-right font-data">{item.quantity}</TableCell>
                          <TableCell className="text-right font-data">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right font-data">{formatCurrency(item.quantity * item.unit_price)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRemoveOrderItem(idx)}>
                              <X size={16} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={3} className="font-semibold">Toplam</TableCell>
                        <TableCell className="text-right font-data font-semibold">{formatCurrency(orderTotal)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="space-y-2">
                <Label>Not</Label>
                <Textarea
                  value={orderForm.notes}
                  onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                  placeholder="Sipariş notu..."
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOrderDialogOpen(false)}>İptal</Button>
                <Button type="submit" className="bg-primary" data-testid="order-submit">Sipariş Oluştur</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
